import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/authService';
import './ProfilePage.css';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, updateUser, logout } = useAuth();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');

  // Form states
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);

  // Password states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState(null);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchProfile();
  }, [isAuthenticated, navigate]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const data = await authService.getProfile();
      const u = data.user || data;
      setProfile(u);
      setName(u.name || '');
      setEmail(u.email || '');
      setPhone(u.phone || '');
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg(null);
    setSavingProfile(true);

    try {
      const updates = {};
      if (name && name !== profile?.name) updates.name = name;
      if (email && email !== profile?.email) updates.email = email;
      if (phone !== (profile?.phone || '')) updates.phone = phone;

      if (Object.keys(updates).length === 0) {
        setProfileMsg({ type: 'info', text: 'No hay cambios para guardar.' });
        setSavingProfile(false);
        return;
      }

      const data = await authService.updateProfile(updates);

      if (data.user) {
        setProfile(data.user);
        updateUser(data.user);
      }
      if (data.token) {
        authService.setAuthToken(data.token);
        localStorage.setItem('authToken', data.token);
      }

      setProfileMsg({ type: 'success', text: 'Perfil actualizado exitosamente.' });
    } catch (err) {
      setProfileMsg({ type: 'error', text: err.message || 'Error al actualizar el perfil.' });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMsg(null);

    if (newPassword.length < 6) {
      setPasswordMsg({ type: 'error', text: 'La nueva contraseña debe tener al menos 6 caracteres.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'Las contraseñas no coinciden.' });
      return;
    }

    setSavingPassword(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
      setPasswordMsg({ type: 'success', text: 'Contraseña actualizada exitosamente.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordMsg({ type: 'error', text: err.message || 'Error al cambiar la contraseña.' });
    } finally {
      setSavingPassword(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-CO', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getInitials = (n) => {
    if (!n) return '?';
    return n.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <div className="profile-loading">
            <div className="profile-spinner"></div>
            <p>Cargando perfil...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-container">
        {/* Header */}
        <div className="profile-header">
          <div className="profile-avatar">
            {getInitials(profile?.name || user?.name)}
          </div>
          <div className="profile-header-info">
            <h1>{profile?.name || user?.name || 'Usuario'}</h1>
            <p>{profile?.email || user?.email}</p>
            <span className="profile-member-since">
              Miembro desde {formatDate(profile?.created_at)}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="profile-tabs">
          <button
            className={`profile-tab ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tab-icon"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            Información Personal
          </button>
          <button
            className={`profile-tab ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tab-icon"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            Seguridad
          </button>
          <button
            className={`profile-tab ${activeTab === 'account' ? 'active' : ''}`}
            onClick={() => setActiveTab('account')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tab-icon"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            Cuenta
          </button>
        </div>

        {/* Tab Content */}
        <div className="profile-content">

          {/* --- Info Tab --- */}
          {activeTab === 'info' && (
            <form className="profile-form" onSubmit={handleSaveProfile}>
              <h2>Información Personal</h2>
              <p className="form-description">Actualiza tus datos de contacto y nombre.</p>

              {profileMsg && (
                <div className={`profile-alert profile-alert-${profileMsg.type}`}>
                  {profileMsg.type === 'success' && '✅ '}
                  {profileMsg.type === 'error' && '❌ '}
                  {profileMsg.type === 'info' && 'ℹ️ '}
                  {profileMsg.text}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="name">Nombre completo</label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Tu nombre"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Correo electrónico</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                />
                <span className="form-hint">Si cambias tu email, se generará un nuevo token de sesión.</span>
              </div>

              <div className="form-group">
                <label htmlFor="phone">Teléfono</label>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+57 300 000 0000"
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary" disabled={savingProfile}>
                  {savingProfile ? (
                    <><span className="btn-spinner"></span> Guardando...</>
                  ) : (
                    'Guardar Cambios'
                  )}
                </button>
              </div>
            </form>
          )}

          {/* --- Security Tab --- */}
          {activeTab === 'security' && (
            <form className="profile-form" onSubmit={handleChangePassword}>
              <h2>Cambiar Contraseña</h2>
              <p className="form-description">Asegúrate de usar una contraseña segura y única.</p>

              {passwordMsg && (
                <div className={`profile-alert profile-alert-${passwordMsg.type}`}>
                  {passwordMsg.type === 'success' && '✅ '}
                  {passwordMsg.type === 'error' && '❌ '}
                  {passwordMsg.text}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="currentPassword">Contraseña actual</label>
                <div className="password-input-wrapper">
                  <input
                    id="currentPassword"
                    type={showCurrentPw ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                  <button type="button" className="password-toggle" onClick={() => setShowCurrentPw(!showCurrentPw)}>
                    {showCurrentPw ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="newPassword">Nueva contraseña</label>
                <div className="password-input-wrapper">
                  <input
                    id="newPassword"
                    type={showNewPw ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    required
                    minLength={6}
                  />
                  <button type="button" className="password-toggle" onClick={() => setShowNewPw(!showNewPw)}>
                    {showNewPw ? '🙈' : '👁️'}
                  </button>
                </div>
                {newPassword && (
                  <div className="password-strength">
                    <div className={`strength-bar ${newPassword.length >= 10 ? 'strong' : newPassword.length >= 6 ? 'medium' : 'weak'}`}></div>
                    <span>{newPassword.length >= 10 ? 'Fuerte' : newPassword.length >= 6 ? 'Media' : 'Débil'}</span>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirmar nueva contraseña</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite la nueva contraseña"
                  required
                />
                {confirmPassword && newPassword !== confirmPassword && (
                  <span className="form-error-text">Las contraseñas no coinciden</span>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={savingPassword || !currentPassword || !newPassword || newPassword !== confirmPassword}
                >
                  {savingPassword ? (
                    <><span className="btn-spinner"></span> Actualizando...</>
                  ) : (
                    'Actualizar Contraseña'
                  )}
                </button>
              </div>
            </form>
          )}

          {/* --- Account Tab --- */}
          {activeTab === 'account' && (
            <div className="profile-form">
              <h2>Información de la Cuenta</h2>
              <p className="form-description">Detalles generales de tu cuenta.</p>

              <div className="account-info-grid">
                <div className="account-info-card">
                  <div className="account-info-icon" style={{ background: 'var(--color-info-soft)', color: 'var(--color-info)' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  </div>
                  <div className="account-info-text">
                    <span className="account-info-label">Nombre</span>
                    <span className="account-info-value">{profile?.name || 'Sin nombre'}</span>
                  </div>
                </div>

                <div className="account-info-card">
                  <div className="account-info-icon" style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent-light)' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  </div>
                  <div className="account-info-text">
                    <span className="account-info-label">Correo</span>
                    <span className="account-info-value">{profile?.email}</span>
                  </div>
                </div>

                <div className="account-info-card">
                  <div className="account-info-icon" style={{ background: 'var(--color-success-soft)', color: 'var(--color-success)' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  </div>
                  <div className="account-info-text">
                    <span className="account-info-label">Miembro desde</span>
                    <span className="account-info-value">{formatDate(profile?.created_at)}</span>
                  </div>
                </div>

                <div className="account-info-card">
                  <div className="account-info-icon" style={{ background: 'var(--color-warning-soft)', color: 'var(--color-warning)' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  </div>
                  <div className="account-info-text">
                    <span className="account-info-label">Último inicio de sesión</span>
                    <span className="account-info-value">{formatDate(profile?.last_login)}</span>
                  </div>
                </div>

                {profile?.phone && (
                  <div className="account-info-card">
                    <div className="account-info-icon" style={{ background: 'var(--color-danger-soft)', color: 'var(--color-danger)' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                    </div>
                    <div className="account-info-text">
                      <span className="account-info-label">Teléfono</span>
                      <span className="account-info-value">{profile.phone}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Quick links */}
              <div className="account-quick-links">
                <h3>Accesos Rápidos</h3>
                <div className="quick-links-grid">
                  <button onClick={() => navigate('/pedidos')} className="quick-link-btn">
                    <span className="quick-link-icon">📦</span>
                    <span>Mis Pedidos</span>
                  </button>
                  <button onClick={() => navigate('/mis-pagos')} className="quick-link-btn">
                    <span className="quick-link-icon">💳</span>
                    <span>Mis Pagos</span>
                  </button>
                  <button onClick={() => navigate('/productos')} className="quick-link-btn">
                    <span className="quick-link-icon">🎲</span>
                    <span>Catálogo</span>
                  </button>
                </div>
              </div>

              {/* Danger zone */}
              <div className="danger-zone">
                <h3>Sesión</h3>
                <p>Cierra la sesión en este dispositivo.</p>
                <button onClick={handleLogout} className="btn-danger">
                  Cerrar Sesión
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
